import { DownloadRounded, MoreVertRounded, TroubleshootRounded } from '@mui/icons-material'
import { Avatar, Box, Button, Card, CardActionArea, CircularProgress, Divider, IconButton, ListItemIcon, Menu, MenuItem, Skeleton, Stack, Typography, useTheme } from '@mui/material'
import { saveAs } from 'file-saver'
import JSZip from 'jszip'
import { useState } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import FlowApiClient from '../../backend/sub_api_clients/FlowApiClient'
import { IContract } from '../../definitions/Contract'
import { IExecutionEnvironmentArtifact } from '../../definitions/NodesExecution'
import { ISettlementSchema } from '../../definitions/SettlementSchema'
import Formatter from '../../helpers/Formatter'
import { useAppSelector } from '../../store/Hooks'
import { changeRoute } from '../../store/slices/SessionSlice'
import SmartCard from '../controls/cards/SmartCard'
import RouteHeader from '../controls/RouteHeader'
import SmartTypography from '../controls/SmartTypography'
import SettlementSubmitModal from '../flows/editor/SettlementSubmitModal'
import useCommand from '../hooks/useCommand'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import CustomIcon from '../icons/CustomIcon'
import Tile from '../layout/Tile'
import RoutingManager from '../routing/Routing'
import { snackbar } from '../services/SnackbarService'
import SettlementDetailsTable from './SettlementDetailsTable'

const normalizeFilename = (name: string): string => {
    const lastDot = name.lastIndexOf('.')

    const extension = name.substring(lastDot)
    const base = name.substring(0, lastDot)

    const lastUnderscore = base.lastIndexOf('_')

    let cleaned = base.substring(0, lastUnderscore)

    cleaned = cleaned.replace(/_/g, ' ')

    return cleaned + extension
}

const ensureUniqueName = (name: string, usedNames: Map<string, number>): string => {
    if (!usedNames.has(name)) {
        usedNames.set(name, 0)
        return name
    }

    let counter = usedNames.get(name)! + 1
    let uniqueName: string

    const dotIndex = name.lastIndexOf('.')
    const base = dotIndex !== -1 ? name.substring(0, dotIndex) : name
    const ext = dotIndex !== -1 ? name.substring(dotIndex) : ''

    do {
        uniqueName = `${base} (${counter})${ext}`
        counter++
    } while (usedNames.has(uniqueName))

    usedNames.set(name, counter - 1)
    usedNames.set(uniqueName, 0)

    return uniqueName
}

const SettlementDetails = () => {
    const dispatch = useDispatch()
    const theme = useTheme()
    const [downloadingArtifactId, setDownloadingArtifactId] = useState<string>()
    const params = RoutingManager.getParams<{ settlementId: string, contractId: number, eeId: string }>(useAppSelector(s => s.session.routeParams))
    const [openSettlementSubmitModal, setOpenSettlementSubmitModal] = useState(false)

    const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null)
    const openMenu = Boolean(anchorElMenu)

    const { status: settlementStatus, response: settlementResponse } = useQuery({
        queryKey: buildQueryKey('settlement-details', params.settlementId),
        queryFn: () => ApiClient.getSettlementDetails(params.settlementId),
        cache: true
    })

    const { status: approvalStatus, response: approval } = useQuery({
        queryKey: buildQueryKey('get-approval-' + params.settlementId),
        queryFn: () => ApiClient.getEntityApproval(params.settlementId),
        cache: true
    })

    const { status: contractStatus, response: contractResponse } = useQuery({
        queryKey: buildQueryKey('get-contract', params.contractId),
        queryFn: async () => await ApiClient.getContractInfo(params.contractId),
        cache: true
    })

    const { status: artifactsStatus, response: artifactsResponse } = useQuery({
        queryKey: buildQueryKey('get-all-artifacts', params.eeId),
        queryFn: async () => await FlowApiClient.getAllArtifacts(params.eeId),
        cache: true
    })

    const { status: downloadAllArtifactsStatus, execute: downloadAllArtifacts } = useCommand({
        commandFn: () => FlowApiClient.downloadAllArtifacts(settlementResponse.settlement.executionEnvironmentId),
        onFinish: result => {
            const urls = result.response
            if (urls?.length > 0)
                downloadFilesAsZip(result.response)
            else
                snackbar.show({ text: 'This settlement has no artifacts', severity: 'info' })
        }
    })

    const { status: downloadArtifactStatus, execute: downloadArtifact } = useCommand({
        commandFn: (artifactId: string) => FlowApiClient.downloadArtifact(settlementResponse.settlement.executionEnvironmentId, artifactId),
        onFinish: result => {
            window.open(result.response)
        }
    })

    const { status: goToAuditStatus, execute: goToAudit } = useCommand({
        commandFn: () => FlowApiClient.getFlowExecutionEnvironment(params.eeId),
        onFinish: result => {
            const executionEnvironment = result.response
            const flowId = executionEnvironment.flow.uuid

            dispatch(changeRoute({ route: 'flow', params: { flowId: flowId, eeId: params.eeId } }))
        }
    })

    const downloadFilesAsZip = async (urls: string[]) => {
        const zip = new JSZip()

        const usedNames = new Map<string, number>()

        const filePromises = urls.map(async url => {
            try {
                const response = await fetch(url)
                if (!response.ok) throw new Error(`Failed to fetch ${url}`)

                const data = await response.arrayBuffer()
                const urlObj = new URL(url)
                const fileName = urlObj.pathname.split('/').pop()

                let normalizedName = normalizeFilename(fileName)
                normalizedName = ensureUniqueName(normalizedName, usedNames)

                zip.file(normalizedName, data)
            } catch (err) {
                console.warn(`Skipping ${url}:`, err)
            }
        })

        await Promise.all(filePromises)

        const content = await zip.generateAsync({ type: 'blob' })
        saveAs(content, `settlement-package-${contract.name}.zip`)
    }

    const onDownloadSingleArtifact = async (artifact: IExecutionEnvironmentArtifact) => {
        setDownloadingArtifactId(artifact.uuid)

        await downloadArtifact(artifact.uuid)

        setDownloadingArtifactId(null)
    }

    const renderBlockText = (text: string, color?: string, rightRender?: JSX.Element) => {
        return (
            <Box
                component='span'
                sx={{
                    backgroundColor: theme.palette.background.smartable,
                    borderRadius: '4px',
                    padding: 0.5,
                    border: (color && !isLoading) && '1px solid ' + color,
                    fontWeight: '500',
                    display: 'inline-block'
                }}>
                {isLoading
                    ? <Skeleton variant='text' sx={{ fontSize: 'body2.fontSize', width: 120 }} />
                    : (
                            <Box flexDirection='row' flex={1} display='flex' justifyContent='center' alignItems='center'>
                                {rightRender}
                                <Typography variant='body2' fontWeight={500} color={color}>{text}</Typography>
                            </Box>
                        )}
            </Box>
        )
    }

    const renderFileCards = (artifacts: IExecutionEnvironmentArtifact[]) => {
        const usedNames = new Map<string, number>()

        return artifacts.map(artifact => {
            const isLoadingCard = downloadAllArtifactsStatus === 'loading' || (downloadArtifactStatus === 'loading' && artifact.uuid === downloadingArtifactId)

            let normalizedName = normalizeFilename(artifact.reference.fileKey)
            normalizedName = ensureUniqueName(normalizedName, usedNames)

            return (
                <Card
                    key={artifact.uuid}
                    sx={{
                        minWidth: 'calc(var(--unit)*38)',
                        maxWidth: 'calc(var(--unit)*38)',
                        position: 'relative'
                    }}>
                    {isLoadingCard && (
                        <CardActionArea sx={{ padding: 1, position: 'absolute' }} disabled>
                            <Box
                                flexDirection='row'
                                display='flex'
                                gap={1}
                                alignItems='center'
                                justifyContent='center'>
                                <CircularProgress />
                            </Box>
                        </CardActionArea>
                    )}
                    <CardActionArea
                        sx={{ padding: 2, opacity: isLoadingCard ? 0.5 : 1 }}
                        disabled={isLoading}
                        onClick={() => onDownloadSingleArtifact(artifact)}>
                        <Box flexDirection='row' display='flex' gap={1} alignItems='center'>
                            <Box minWidth='calc(var(--unit)*5)' height='calc(var(--unit)*5)'>
                                <CustomIcon name='generic' />
                            </Box>
                            <Box overflow='hidden'>
                                <SmartTypography
                                    variant='body2'
                                    fontWeight='bold'
                                    fontSize={14}
                                    charLimit={20}>
                                    {normalizedName}
                                </SmartTypography>
                                <SmartTypography variant='caption'>
                                    {artifact.reference.type}
                                </SmartTypography>
                            </Box>
                        </Box>
                    </CardActionArea>
                </Card>
            )
        })
    }

    const isLoading = settlementStatus === 'loading' || approvalStatus === 'loading' || contractStatus === 'loading' || artifactsStatus === 'loading'
    const settlement: ISettlementSchema & { userName: string } = settlementResponse?.settlement ?? null
    const contract: IContract = contractResponse ?? null

    return (
        <>
            <Tile.Container>
                <Box style={{ flex: 0, display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Box>
                        <RouteHeader primary='Settlement Details' />
                    </Box>
                    <Stack direction='row' spacing={2} alignItems='center'>
                        <Box>
                            <Button variant='contained' size='small' onClick={() => setOpenSettlementSubmitModal(true)}>Manage</Button>
                        </Box>
                        <Box>
                            <IconButton size='small' onClick={e => setAnchorElMenu(e.currentTarget)}>
                                <MoreVertRounded fontSize='small' />
                            </IconButton>
                        </Box>
                    </Stack>
                    <Menu
                        anchorEl={anchorElMenu}
                        open={openMenu}
                        onClose={() => setAnchorElMenu(null)}
                        onClick={() => setAnchorElMenu(null)}
                        aria-controls={openMenu ? 'simple-menu' : undefined}>
                        <MenuItem onClick={goToAudit}>
                            <ListItemIcon>
                                {goToAuditStatus === 'loading' ? <CircularProgress size={20} /> : <TroubleshootRounded />}
                            </ListItemIcon>
                            Audit
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={downloadAllArtifacts}>
                            <ListItemIcon>
                                {artifactsStatus === 'loading' ? <CircularProgress size={20} /> : <DownloadRounded />}
                            </ListItemIcon>
                            Download Package
                        </MenuItem>
                    </Menu>
                </Box>
                <Box marginTop={1} flex={1} display='flex' flexDirection='column' gap={5}>
                    <Box flex={1} marginTop={1} display='flex' flexDirection='row' gap={5}>
                        <SmartCard flex={1}>
                            <Typography variant='h6'>Activity Summary</Typography>
                            <Typography marginTop={2} lineHeight={2.5}>
                                This
                                {' '}
                                {renderBlockText(settlement?.type, settlement?.type ? theme.palette.success.main : theme.palette.info.main)}
                                {' '}
                                settlement was run on
                                {' '}
                                {renderBlockText(Formatter.date(new Date(settlement?.runAt), 'full'))}
                                {' '}
                                for the
                                {' '}
                                {renderBlockText(contract?.name)}
                                {' '}
                                between
                                {' '}
                                {renderBlockText(contract?.payer?.name ? contract.payer.name.replace(/\b\w/g, c => c.toUpperCase()) : '')}
                                {' '}
                                and
                                {' '}
                                {renderBlockText(contract?.counterparty?.name)}
                                .
                                {' '}
                                The settlement was generated by
                                {' '}
                                {renderBlockText(settlement?.userName, null, <Avatar sx={{ height: 20, width: 20, marginRight: 1, bgcolor: theme.palette.action.selected }} variant='circular' />)}
                                {' '}
                                utilizing Engine version
                                {' '}
                                {renderBlockText(settlement?.flowVersion?.toString())}
                                .
                                This was the
                                {' '}
                                {renderBlockText(settlement?.versioning?.version?.toString() + '° ')}
                                {' '}
                                settlement has been run for performance period
                                {' '}
                                {renderBlockText(Formatter.date(new Date(settlement?.periodStart), 'mmm dd, yyyy'))}
                                {' '}
                                -
                                {' '}
                                {renderBlockText(Formatter.date(new Date(settlement?.periodEnd), 'mmm dd, yyyy'))}
                                .
                            </Typography>
                        </SmartCard>
                    </Box>
                    <Box flex={2} marginTop={1}>
                        <SettlementDetailsTable />
                    </Box>
                    <Box flex={2} marginTop={1} marginBottom={2}>
                        <SmartCard>
                            <Typography variant='h6' marginBottom={2}>Artifacts</Typography>
                            <Tile.Container>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing(2) }}>
                                    {renderFileCards(artifactsResponse ?? [])}
                                </Box>
                            </Tile.Container>
                        </SmartCard>
                    </Box>
                </Box>
                {settlementStatus === 'success' && <SettlementSubmitModal approval={approval} settlement={settlement} open={openSettlementSubmitModal} setOpen={setOpenSettlementSubmitModal} />}
            </Tile.Container>
        </>
    )
}

export default SettlementDetails
